import { createStore } from "devextreme-aspnet-data-nojquery";
import {
  setupRequestAsync,
  fetchApi,
  fetchApiOrThrow,
} from "~/utils";
import { squireApiConfig } from "~/config";

const { url } = squireApiConfig;

const gridLayoutsUrl = `${url}/v1/grid-layouts`;

// Saved grid layouts for the signed-in user. Every endpoint is scoped server-side by the caller's
// Entra oid, so nothing here sends a user id — an id in the request would not be trusted anyway.
//
// layoutKey travels as a query-string value rather than a path segment: keys carry a dot
// ("Companies.List"), and a dot in the final path segment invites extension handling.
export class GridLayoutsApiService {
  getGridLayoutsDataSource(layoutKey) {
    return createStore({
      key: "id",
      loadUrl: `${gridLayoutsUrl}/?layoutKey=${encodeURIComponent(layoutKey)}`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  // Having set no default is the normal state, so the endpoint answers it with 204 No Content and
  // fetchResponseHandler hands that back as an empty string - which the layout hook reads as "no
  // layout to apply". It is NOT a 404: the shared result processor maps Outcome.NotFound to 400, and
  // any 4xx makes fetchResponseHandler raise an error toast, so the controller returns 204 instead.
  // fetchApi rather than fetchApiOrThrow because this runs inside DevExtreme's mount path, where a
  // rejection must not escape.
  async getDefaultGridLayout(layoutKey) {
    const fetchUrl = `${gridLayoutsUrl}/default?layoutKey=${encodeURIComponent(layoutKey)}`;
    return await fetchApi(fetchUrl, "GET");
  }

  async getGridLayout(gridLayoutId) {
    const fetchUrl = `${gridLayoutsUrl}/${gridLayoutId}`;
    return await fetchApi(fetchUrl, "GET");
  }

  // The writes below all use fetchApiOrThrow. Name uniqueness is enforced on the entity's hooks and
  // comes back as a 400 carrying the domain's message; fetchApi would swallow it and a rejected save
  // would look successful.
  async createGridLayout(layoutKey, name, data) {
    const fetchUrl = `${gridLayoutsUrl}/`;
    const formBody = new URLSearchParams({
      layoutKey: layoutKey,
      name: name,
      data: data,
    });
    return await fetchApiOrThrow(fetchUrl, "POST", formBody);
  }

  // A field left out means "leave it alone" server-side, which is what lets the rename popup send a
  // name only and "update to current view" send data only. An omitted field must therefore stay
  // omitted rather than being sent empty, which would fail the domain's Required check.
  async updateGridLayout(gridLayoutId, { name, data } = {}) {
    const fetchUrl = `${gridLayoutsUrl}/`;
    const formBody = new URLSearchParams({
      key: gridLayoutId,
    });
    if (name !== undefined && name !== null) {
      formBody.append("name", name);
    }
    if (data !== undefined && data !== null) {
      formBody.append("data", data);
    }
    return await fetchApiOrThrow(fetchUrl, "PUT", formBody);
  }

  // One call, not a clear-then-set pair: demoting the previous default is the entity's job.
  async setGridLayoutDefault(gridLayoutId, isDefault) {
    const fetchUrl = `${gridLayoutsUrl}/default`;
    const formBody = new URLSearchParams({
      key: gridLayoutId,
      isDefault: isDefault,
    });
    return await fetchApiOrThrow(fetchUrl, "PUT", formBody);
  }

  async deleteGridLayout(gridLayoutId) {
    const fetchUrl = `${gridLayoutsUrl}/`;
    const formBody = new URLSearchParams({
      key: gridLayoutId,
    });
    return await fetchApiOrThrow(fetchUrl, "DELETE", formBody);
  }

  // Both keys are sent even though the client can compare them itself. The mismatch refusal is a
  // boundary and belongs on the server; a client-side check is only there to fail faster.
  async importGridLayout(targetLayoutKey, fileLayoutKey, name, data) {
    const fetchUrl = `${gridLayoutsUrl}/import`;
    const formBody = new URLSearchParams({
      targetLayoutKey: targetLayoutKey,
      fileLayoutKey: fileLayoutKey,
      name: name,
      data: data,
    });
    return await fetchApiOrThrow(fetchUrl, "POST", formBody);
  }
}
